import React, { useEffect, useState } from "react";
import { getData } from "../../../utils/SamuiUtils";
import TimeUnitQuickModal from "../Modal/TimeUnitQuickModal";

export default function ServiceDetails({ serviceDetail, setServiceDetail }) {
  const FALLBACK_CONTRACT_UNITS = [
    { value: 1, label: "วัน" },
    { value: 2, label: "สัปดาห์" },
    { value: 3, label: "เดือน" },
    { value: 4, label: "ปี" },
    { value: 5, label: "ครั้ง" },
  ];
  const FALLBACK_SERVICE_UNITS = [
    { value: 1, label: "รอบ" },
    { value: 2, label: "เดือน / ครั้ง" },
    { value: 3, label: "ปี / ครั้ง" },
  ];
  const [contractUnits, setContractUnits] = useState(FALLBACK_CONTRACT_UNITS);
  const [serviceUnits, setServiceUnits] = useState(FALLBACK_SERVICE_UNITS);
  const [showTimeUnitModal, setShowTimeUnitModal] = useState(false);
  const [timeUnitType, setTimeUnitType] = useState("CONTRACT");

  const handleOpenTimeUnit = (type) => {
    setTimeUnitType(type);
    setShowTimeUnitModal(true);
  };

  useEffect(() => {
    let isMounted = true;

    const normalizeList = (res) => {
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.data)) return res.data;
      return [];
    };

    const fetchUnits = async () => {
      try {
        const contractRes = await getData(
          "Tb_Set_TimeUnit",
          "UPPER(TimeUnit_Type) = 'CONTRACT' AND UPPER(TimeUnit_Status) = 'Y' ORDER BY TimeUnit_Sort, TimeUnit_Name"
        );
        const serviceRes = await getData(
          "Tb_Set_TimeUnit",
          "UPPER(TimeUnit_Type) = 'SERVICE' AND UPPER(TimeUnit_Status) = 'Y' ORDER BY TimeUnit_Sort, TimeUnit_Name"
        );

        const contractList = normalizeList(contractRes).map((item) => ({
          value: item.TimeUnit_Code,
          label: item.TimeUnit_Name,
        }));
        const serviceList = normalizeList(serviceRes).map((item) => ({
          value: item.TimeUnit_Code,
          label: item.TimeUnit_Name,
        }));

        if (!isMounted) return;
        if (contractList.length > 0) setContractUnits(contractList);
        if (serviceList.length > 0) setServiceUnits(serviceList);
      } catch (error) {
        console.error("Error fetching time units:", error);
        if (!isMounted) return;
        setContractUnits(FALLBACK_CONTRACT_UNITS);
        setServiceUnits(FALLBACK_SERVICE_UNITS);
      }
    };

    fetchUnits();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleRefreshUnits = async () => {
    try {
      const contractRes = await getData(
        "Tb_Set_TimeUnit",
        "UPPER(TimeUnit_Type) = 'CONTRACT' AND UPPER(TimeUnit_Status) = 'Y' ORDER BY TimeUnit_Sort, TimeUnit_Name"
      );
      const serviceRes = await getData(
        "Tb_Set_TimeUnit",
        "UPPER(TimeUnit_Type) = 'SERVICE' AND UPPER(TimeUnit_Status) = 'Y' ORDER BY TimeUnit_Sort, TimeUnit_Name"
      );
      const normalizeList = (res) =>
        Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      const refreshedContract = normalizeList(contractRes).map((item) => ({
        value: item.TimeUnit_Code,
        label: item.TimeUnit_Name,
      }));
      const refreshedService = normalizeList(serviceRes).map((item) => ({
        value: item.TimeUnit_Code,
        label: item.TimeUnit_Name,
      }));
      if (refreshedContract.length > 0) setContractUnits(refreshedContract);
      if (refreshedService.length > 0) setServiceUnits(refreshedService);
    } catch (error) {
      console.error("Error refreshing time units:", error);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow space-y-6">
      <h3 className="font-bold text-gray-700 text-lg">รายละเอียดการบริการ</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* ระยะสัญญา */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-600">
              ระยะสัญญา
            </label>
            <button
              type="button"
              className="text-lg font-semibold text-[#E13F57] hover:text-[#c9354a]"
              title="เพิ่มหน่วย"
              onClick={() => handleOpenTimeUnit("CONTRACT")}
            >
              +
            </button>
          </div>
          <div className="flex">
            <input
              type="number"
              min="0"
              value={serviceDetail.Contract_Period || ""}
              onKeyDown={(e) => {
                if (e.key === "-" || e.key === "e") {
                  e.preventDefault();
                }
              }}
              onChange={(e) =>
                setServiceDetail((prev) => ({
                  ...prev,
                  Contract_Period: e.target.value < 0 ? 0 : e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded-l-lg px-4 py-3 text-gray-700 
                        focus:outline-none focus:ring-2 focus:ring-[#E13F57] focus:border-[#E13F57]"
            />

            <select
              className="border border-gray-300 px-3 bg-gray-50 text-gray-700 
                         focus:outline-none focus:ring-2 focus:ring-[#E13F57] focus:border-[#E13F57]"
              value={serviceDetail.Contract_Unit || ""}
              onChange={(e) =>
                setServiceDetail((prev) => ({
                  ...prev,
                  Contract_Unit: e.target.value,
                }))
              }
            >
              <option value="">เลือกหน่วย</option>
              {contractUnits.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-600 whitespace-nowrap">
              จำนวนบริการตรวจเช็ค
            </label>
            <button
              type="button"
              className="text-lg font-semibold text-[#E13F57] hover:text-[#c9354a]"
              title="เพิ่มหน่วย"
              onClick={() => handleOpenTimeUnit("SERVICE")}
            >
              +
            </button>
          </div>

          <div className="flex">
            <input
              type="number"
              min="0"
              value={serviceDetail.Service_Time || ""}
              onKeyDown={(e) => {
                if (e.key === "-" || e.key === "e") e.preventDefault();
              }}
              onChange={(e) =>
                setServiceDetail((prev) => ({
                  ...prev,
                  Service_Time: e.target.value < 0 ? 0 : e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded-l-lg px-4 py-3 text-gray-700 
                        focus:outline-none focus:ring-2 focus:ring-[#E13F57] focus:border-[#E13F57]"
            />

            <select
              className="border border-gray-300 px-3 bg-gray-50 text-gray-700 
                        focus:outline-none focus:ring-2 focus:ring-[#E13F57] focus:border-[#E13F57]"
              value={serviceDetail.Service_Unit || ""}
              onChange={(e) =>
                setServiceDetail((prev) => ({
                  ...prev,
                  Service_Unit: Number(e.target.value),
                }))
              }
            >
              <option value="">เลือกหน่วย</option>
              {serviceUnits.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
        </div>

      </div>

      {/* เงื่อนไขการชำระเงิน */}
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1 whitespace-nowrap">
          เงื่อนไขการชำระเงิน
        </label>
        <textarea
          rows={4}
          value={serviceDetail.Payment_Condition || ""}
          onChange={(e) =>
            setServiceDetail((prev) => ({
              ...prev,
              Payment_Condition: e.target.value,
            }))
          }
          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-700 
                     focus:outline-none focus:ring-2 focus:ring-[#E13F57] focus:border-[#E13F57]"
        />
      </div>

      <TimeUnitQuickModal
        open={showTimeUnitModal}
        onClose={() => setShowTimeUnitModal(false)}
        defaultType={timeUnitType}
        onSaved={handleRefreshUnits}
      />
    </div>
  );
}
